'use client';

import { useCallback } from 'react';
import { WalletReadyState } from '@solana/wallet-adapter-base';
import { MagicAuthLoginOptions } from 'src/utils/magicAuthAdapter';
import createMagicWalletAdapter from 'src/utils/magicAuthAdapter';
import { AppKitAdapter } from 'src/utils/AppKitAdapter';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import {
	DriftCustomWalletName,
	DRIFT_CUSTOM_WALLET_OPTIONS,
} from 'src/constants/wallets';
import { useLinkWallet } from './useLinkWallet';
import { Provider } from '@reown/appkit-adapter-solana/react';
import useSyncedRef from '../utils/useSyncedRef';

type AppKitWalletOptions = {
	provider: Provider;
	address: string;
};

const useConnectCustomWallet = () => {
	const connection = useDriftStore((s) => s.connection.current);

	const set = useDriftStore((s) => s.set);

	const { linkWalletToDriftClient } = useLinkWallet();
	const linkWalletToDriftClientRef = useSyncedRef(linkWalletToDriftClient); // using a ref to avoid race conditions with the linkWalletToDriftClient callback updates

	const connectToMagicAuth = useCallback(
		async (loginOptions: MagicAuthLoginOptions) => {
			const endpoint = connection?.rpcEndpoint as string;

			const magicWallet = createMagicWalletAdapter(endpoint);

			// disconnect and error handlers are added in useKeepWalletsInSync
			// Remove any existing 'connect' listener to avoid duplicates
			magicWallet.removeListener('connect', linkWalletToDriftClient);
			magicWallet.on('connect', linkWalletToDriftClient);

			set((state) => {
				// Clean up previous adapter's 'connect' listener if it exists
				if (state.wallet.current.adapter) {
					state.wallet.current.adapter.removeListener(
						'connect',
						linkWalletToDriftClient
					);
				}
				state.wallet.current.adapter = magicWallet;
				state.wallet.isMagicAuth = true;
			});

			return await magicWallet.connect(loginOptions);
		},
		[connection, set, linkWalletToDriftClient]
	);

	const connectToAppKitWallet = useCallback(
		async (appKitOptions: AppKitWalletOptions) => {
			const appKitWallet = new AppKitAdapter(
				appKitOptions.address,
				appKitOptions.provider
			);
			appKitWallet.connected = true;
			appKitWallet.readyState = WalletReadyState.Installed;

			set((state) => {
				state.wallet.current.adapter = appKitWallet;
				state.wallet.isAppKit = true;
			});

			await linkWalletToDriftClientRef.current();
		},
		[set]
	);

	const connectWithCustomWallet = useCallback(
		async (
			customWalletName: DriftCustomWalletName,
			opts?: AppKitWalletOptions | MagicAuthLoginOptions
		) => {
			switch (customWalletName) {
				case DRIFT_CUSTOM_WALLET_OPTIONS.APPKIT_WALLET_ADAPTER_NAME:
					return connectToAppKitWallet(opts as AppKitWalletOptions);
				case DRIFT_CUSTOM_WALLET_OPTIONS.MAGIC_AUTH_WALLET_ADAPTER_NAME:
					return connectToMagicAuth(opts as MagicAuthLoginOptions);
				default: {
					throw new Error(`Unhandled custom wallet name: ${customWalletName}`);
				}
			}
		},
		[connectToAppKitWallet, connectToMagicAuth]
	);

	return {
		connectWithCustomWallet,
	};
};

export default useConnectCustomWallet;
